import { ChipInput } from '@/components/ChipInput';
import { useT } from '@/i18n';
import { BlockTitle, List, ListItem } from 'framework7-react';
import { useState } from 'react';

/**
 * Day-of-week + time-range editor for a promotion's `weeklySchedule`. This is
 * the restaurant equivalent of the salon "off-peak hours": the promotion only
 * applies when the order falls on one of the chosen days within one of its time
 * windows (evaluated in the restaurant timezone by the backend engine).
 *
 * Emits the flattened `ScheduleWindow[]` whenever the draft changes - an empty
 * array means "no weekly restriction" (always-on within the date window), which
 * the parent maps to `weeklySchedule: null`.
 */

export interface ScheduleWindow {
  dayOfWeek: number; // 0=Sun … 6=Sat
  start: string; // HH:mm (restaurant-local)
  end: string; // HH:mm (restaurant-local)
}

type Win = { start: string; end: string };
type Draft = Record<number, { enabled: boolean; windows: Win[] }>;

// Operators read the week Monday-first; dayOfWeek stays 0=Sun … 6=Sat on the
// wire. Labels come from the shared localized day-name array (0=Sun indexed).
const WEEK_DOW = [1, 2, 3, 4, 5, 6, 0];

function seed(initial: ScheduleWindow[]): Draft {
  const draft: Draft = {};
  for (const dow of WEEK_DOW) {
    const windows = initial
      .filter((w) => w.dayOfWeek === dow)
      .map((w) => ({ start: w.start, end: w.end }));
    draft[dow] = { enabled: windows.length > 0, windows };
  }
  return draft;
}

function flatten(draft: Draft): ScheduleWindow[] {
  const out: ScheduleWindow[] = [];
  for (const dow of WEEK_DOW) {
    const day = draft[dow];
    if (!day.enabled) continue;
    for (const w of day.windows) out.push({ dayOfWeek: dow, start: w.start, end: w.end });
  }
  return out;
}

export function WeeklyScheduleSection({
  initial,
  onChange,
}: {
  initial: ScheduleWindow[];
  onChange: (windows: ScheduleWindow[]) => void;
}) {
  const t = useT();
  const tp = t.promotions;
  const dayNames = t.reservationSetup.dayNamesLong;
  const [hasSchedule, setHasSchedule] = useState(initial.length > 0);
  const [draft, setDraft] = useState<Draft>(() => seed(initial));

  const commit = (next: Draft) => {
    setDraft(next);
    onChange(flatten(next));
  };

  const patchDay = (dow: number, next: Partial<{ enabled: boolean; windows: Win[] }>) =>
    commit({ ...draft, [dow]: { ...draft[dow], ...next } });

  const setEnabled = (dow: number, enabled: boolean) => {
    const day = draft[dow];
    // Turning a day on with no windows yet → seed a sensible default.
    const windows =
      enabled && day.windows.length === 0 ? [{ start: '09:00', end: '17:00' }] : day.windows;
    patchDay(dow, { enabled, windows });
  };

  const setWin = (dow: number, i: number, key: keyof Win, value: string) =>
    patchDay(dow, {
      windows: draft[dow].windows.map((w, idx) => (idx === i ? { ...w, [key]: value } : w)),
    });

  const addWin = (dow: number) =>
    patchDay(dow, { windows: [...draft[dow].windows, { start: '17:00', end: '22:00' }] });

  const removeWin = (dow: number, i: number) =>
    patchDay(dow, { windows: draft[dow].windows.filter((_, idx) => idx !== i) });

  const toggleSchedule = (on: boolean) => {
    setHasSchedule(on);
    // Off → no weekly restriction. On → emit whatever's already drafted.
    onChange(on ? flatten(draft) : []);
  };

  return (
    <>
      <BlockTitle>{tp.daysAndHours}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem title={tp.limitToDays} footer={tp.limitToDaysFooter}>
          {/* Plain controlled checkbox + F7's `.toggle` markup (not <Toggle>) -
              the component's toggleChange event doesn't always wire inside a
              re-rendering list, so React owns the checkbox directly here. */}
          <label className="toggle" slot="after">
            <input
              type="checkbox"
              checked={hasSchedule}
              onChange={(e) => toggleSchedule(e.target.checked)}
            />
            <span className="toggle-icon" />
          </label>
        </ListItem>
      </List>

      {hasSchedule &&
        WEEK_DOW.map((dow) => {
          const day = draft[dow];
          const label = dayNames[dow];
          return (
            <div key={dow}>
              <BlockTitle>{label}</BlockTitle>
              <List strongIos insetIos dividersIos>
                <ListItem title={tp.active}>
                  <label className="toggle" slot="after">
                    <input
                      type="checkbox"
                      checked={day.enabled}
                      onChange={(e) => setEnabled(dow, e.target.checked)}
                    />
                    <span className="toggle-icon" />
                  </label>
                </ListItem>
                {day.enabled &&
                  day.windows.map((w, i) => (
                    <ListItem
                      key={i}
                      title={day.windows.length > 1 ? tp.windowLabel(i + 1) : tp.hours}
                    >
                      <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ChipInput
                          type="time"
                          value={w.start}
                          onChange={(v) => setWin(dow, i, 'start', v)}
                          aria-label={tp.startAria(label)}
                        />
                        <span style={{ color: 'var(--f7-list-item-subtitle-text-color)' }}>–</span>
                        <ChipInput
                          type="time"
                          value={w.end}
                          onChange={(v) => setWin(dow, i, 'end', v)}
                          aria-label={tp.endAria(label)}
                        />
                        <i
                          className="f7-icons"
                          style={{ fontSize: 20, color: '#ff3b30', cursor: 'pointer' }}
                          onClick={() => removeWin(dow, i)}
                          aria-label={tp.removeWindow}
                        >
                          minus_circle
                        </i>
                      </div>
                    </ListItem>
                  ))}
                {day.enabled && (
                  <ListItem title={tp.addWindow} link="#" onClick={() => addWin(dow)}>
                    <i slot="media" className="f7-icons" style={{ color: 'var(--f7-theme-color)' }}>
                      plus_circle
                    </i>
                  </ListItem>
                )}
              </List>
            </div>
          );
        })}
    </>
  );
}
